import { useEffect, useState } from 'react';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useFinance } from '@/hooks/useFinance';
import {
  FINANCE_ALERT_META,
  type FinanceAlertKey,
  type FinanceAlertPrefs,
} from '@/lib/finance';
import {
  LEGAL_FORMS,
  INVOICE_LANGUAGES,
  INVOICE_TEMPLATES,
  buildInvoiceNumber,
  type CompanyProfile,
} from '@/lib/companyProfile';
import { FinanceSection, Field, inputClass } from '../components/FinanceUI';
import { useMerchantCurrency } from '@/hooks/useMerchantCurrency';
import ImagePicker from '@/components/feature/ImagePicker';

export default function FinanceParametresPage() {
  const { profile, loading, save } = useCompanyProfile();
  const finance = useFinance();
  const { alertPrefs, saveSettings } = finance;
  // Devise unique du marchand (définie dans Paramètres → Profil).
  const merchantCurrency = useMerchantCurrency();

  const [form, setForm] = useState<CompanyProfile>(profile);
  const [prefs, setPrefs] = useState<FinanceAlertPrefs>(alertPrefs);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);

  useEffect(() => {
    setForm({ ...profile, currency: merchantCurrency });
  }, [profile, merchantCurrency]);

  useEffect(() => {
    setPrefs(alertPrefs);
  }, [alertPrefs]);

  const set = <K extends keyof CompanyProfile>(key: K, value: CompanyProfile[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await save(form);
      await saveSettings({ alerts: prefs, currency: merchantCurrency, invoice_prefix: form.invoice_prefix });
      setMsg({ type: 'success', text: 'Paramètres de facturation enregistrés.' });
    } catch (e) {
      setMsg({ type: 'error', text: e instanceof Error ? e.message : 'Erreur lors de l’enregistrement' });
    } finally {
      setBusy(false);
    }
  };

  const nextNumberPreview = buildInvoiceNumber(form, form.next_invoice_seq || 1);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div className="space-y-5">
      {msg && (
        <div className={`px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-primary-50 text-primary-800' : 'bg-red-50 text-red-600'}`}>
          <i className={msg.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
          {msg.text}
        </div>
      )}

      <FinanceSection title="Identité de l’entreprise" icon="ri-building-2-line">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Field label="Nom de l’entreprise"><input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} className={inputClass} placeholder="Ex : Zifek Store SARL" /></Field>
          <Field label="Forme juridique">
            <select value={form.legal_form} onChange={(e) => set('legal_form', e.target.value)} className={inputClass}>
              <option value="">—</option>
              {LEGAL_FORMS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Logo de l’entreprise">
          <div className="flex items-center gap-2">
            <input value={form.logo_url} onChange={(e) => set('logo_url', e.target.value)} className={inputClass} placeholder="https://..." />
            <button
              type="button"
              onClick={() => setLogoPickerOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-upload-2-line"></i>Téléverser
            </button>
          </div>
          {form.logo_url && (
            <div className="mt-2 w-24 h-16 rounded-lg border border-background-200/70 overflow-hidden bg-white flex items-center justify-center">
              <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
            </div>
          )}
        </Field>
      </FinanceSection>

      <FinanceSection title="Adresse" icon="ri-map-pin-line">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Field label="Adresse"><input value={form.address} onChange={(e) => set('address', e.target.value)} className={inputClass} placeholder="Rue, numéro" /></Field>
          <Field label="Ville"><input value={form.city} onChange={(e) => set('city', e.target.value)} className={inputClass} placeholder="Casablanca" /></Field>
          <Field label="Code postal"><input value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} className={inputClass} placeholder="20000" /></Field>
          <Field label="Pays"><input value={form.country} onChange={(e) => set('country', e.target.value)} className={inputClass} placeholder="Maroc" /></Field>
        </div>
      </FinanceSection>

      <FinanceSection title="Coordonnées" icon="ri-contacts-line">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3">
          <Field label="Téléphone"><input value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputClass} placeholder="05..." /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass} placeholder="contact@..." /></Field>
          <Field label="Site web"><input value={form.website} onChange={(e) => set('website', e.target.value)} className={inputClass} placeholder="https://..." /></Field>
        </div>
      </FinanceSection>

      <FinanceSection title="Informations légales" icon="ri-shield-check-line">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3">
          <Field label="ICE"><input value={form.ice} onChange={(e) => set('ice', e.target.value)} className={inputClass} placeholder="000000000000000" /></Field>
          <Field label="Identifiant Fiscal (IF)"><input value={form.if_number} onChange={(e) => set('if_number', e.target.value)} className={inputClass} /></Field>
          <Field label="Taxe Professionnelle (TP)"><input value={form.tp_number} onChange={(e) => set('tp_number', e.target.value)} className={inputClass} /></Field>
          <Field label="Registre de Commerce (RC)"><input value={form.rc_number} onChange={(e) => set('rc_number', e.target.value)} className={inputClass} /></Field>
          <Field label="CNSS"><input value={form.cnss_number} onChange={(e) => set('cnss_number', e.target.value)} className={inputClass} /></Field>
        </div>
      </FinanceSection>

      <FinanceSection title="Fiscalité (TVA)" icon="ri-percent-line">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 items-end">
          <label className="flex items-center gap-3 cursor-pointer mb-3">
            <input type="checkbox" checked={form.vat_subject} onChange={(e) => set('vat_subject', e.target.checked)} className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
            <span className="text-sm font-medium text-foreground-800">Assujetti à la TVA</span>
          </label>
          <Field label="Taux de TVA par défaut (%)">
            <input type="number" min="0" step="0.01" value={form.default_vat_rate} onChange={(e) => set('default_vat_rate', parseFloat(e.target.value) || 0)} className={inputClass} />
          </Field>
        </div>
        <p className="text-[11px] text-foreground-400 flex items-start gap-1">
          <i className="ri-information-line mt-0.5"></i>
          Zifek n’est pas un système de comptabilité certifié. Adaptez ces paramètres à la réglementation applicable.
        </p>
      </FinanceSection>

      <FinanceSection title="Coordonnées bancaires" icon="ri-bank-line">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3">
          <Field label="Banque"><input value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} className={inputClass} placeholder="Attijariwafa..." /></Field>
          <Field label="RIB"><input value={form.rib} onChange={(e) => set('rib', e.target.value)} className={inputClass} /></Field>
          <Field label="IBAN"><input value={form.iban} onChange={(e) => set('iban', e.target.value)} className={inputClass} /></Field>
        </div>
      </FinanceSection>

      <FinanceSection title="Numérotation des factures" icon="ri-hashtag">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3">
          <Field label="Préfixe">
            <div className={`${inputClass} flex items-center gap-2 bg-background-100 text-foreground-700`}>
              <i className="ri-lock-line text-foreground-400"></i><span className="font-medium">{form.invoice_prefix || 'FAC'}</span>
            </div>
          </Field>
          <Field label="Format">
            <div className={`${inputClass} flex items-center gap-2 bg-background-100 text-foreground-700`}>
              <i className="ri-lock-line text-foreground-400"></i><span className="font-medium truncate">{form.invoice_number_format || 'FAC-{YYYY}-{SEQ:5}'}</span>
            </div>
          </Field>
          <Field label="Prochain numéro (séquence)">
            <div className={`${inputClass} flex items-center gap-2 bg-background-100 text-foreground-700`}>
              <i className="ri-lock-line text-foreground-400"></i><span className="font-medium">{form.next_invoice_seq || 1}</span>
            </div>
          </Field>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-arrow-right-line text-primary-500"></i>
          Prochaine facture&nbsp;: <span className="font-semibold text-foreground-900">{nextNumberPreview}</span>
        </div>
        <p className="text-[11px] text-foreground-400 mt-1.5 flex items-start gap-1">
          <i className="ri-information-line mt-0.5"></i>
          Jetons : {'{PREFIX} {YYYY} {YY} {MM} {SEQ:5}'}. La numérotation est continue et incrémentée automatiquement à chaque facture — elle n’est pas modifiable.
        </p>
      </FinanceSection>

      <FinanceSection title="Conditions & modes de paiement" icon="ri-hand-coin-line">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Field label="Conditions de paiement"><input value={form.payment_terms} onChange={(e) => set('payment_terms', e.target.value)} className={inputClass} placeholder="Paiement à 30 jours" /></Field>
          <Field label="Modes de paiement acceptés"><input value={form.payment_methods} onChange={(e) => set('payment_methods', e.target.value)} className={inputClass} placeholder="Espèces, Virement, CMI..." /></Field>
        </div>
      </FinanceSection>

      <FinanceSection title="Modèle, langue & devise" icon="ri-file-text-line">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3">
          <Field label="Modèle de facture">
            <select value={form.invoice_template} onChange={(e) => set('invoice_template', e.target.value as CompanyProfile['invoice_template'])} className={inputClass}>
              {INVOICE_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Langue de facture">
            <select value={form.invoice_language} onChange={(e) => set('invoice_language', e.target.value as CompanyProfile['invoice_language'])} className={inputClass}>
              {INVOICE_LANGUAGES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
          </Field>
          <Field label="Devise">
            <div className={`${inputClass} flex items-center gap-2 text-foreground-700`} title="Devise unique de votre compte">
              <i className="ri-lock-line text-foreground-400"></i>
              <span className="font-medium">{merchantCurrency}</span>
            </div>
          </Field>
        </div>
      </FinanceSection>

      <FinanceSection title="Mentions légales & pied de facture" icon="ri-file-shield-2-line">
        <Field label="Mentions légales"><textarea value={form.legal_mentions} onChange={(e) => set('legal_mentions', e.target.value)} rows={3} maxLength={500} className={inputClass} placeholder="Ex : TVA non applicable, art. 6..." /></Field>
        <Field label="Pied de facture"><textarea value={form.invoice_footer} onChange={(e) => set('invoice_footer', e.target.value)} rows={2} maxLength={500} className={inputClass} placeholder="Ex : Merci de votre confiance." /></Field>
      </FinanceSection>

      <FinanceSection title="Alertes financières" icon="ri-notification-3-line">
        <p className="text-xs text-foreground-500 mb-3">Choisissez les alertes à afficher dans votre tableau de bord Finance.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {FINANCE_ALERT_META.map((a) => (
            <label key={a.key} className="flex items-center gap-3 p-3 rounded-lg border border-background-200/70 bg-background-50 cursor-pointer hover:bg-background-100 transition-colors">
              <input
                type="checkbox"
                checked={prefs[a.key as FinanceAlertKey]}
                onChange={(e) => setPrefs((prev) => ({ ...prev, [a.key]: e.target.checked }))}
                className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer"
              />
              <i className={`${a.icon} text-base text-foreground-500`}></i>
              <span className="text-sm text-foreground-800">{a.label}</span>
            </label>
          ))}
        </div>
      </FinanceSection>

      <div className="sticky bottom-4 flex justify-end">
        <button
          onClick={handleSave}
          disabled={busy}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          Enregistrer les paramètres
        </button>
      </div>

      <ImagePicker
        isOpen={logoPickerOpen}
        onClose={() => setLogoPickerOpen(false)}
        onSelect={(url) => set('logo_url', url)}
        selectedUrl={form.logo_url}
      />
    </div>
  );
}